import { useEffect, useState } from 'react';
import { Button, Group, Stack, Switch, Table, Text, TextInput, Title } from '@mantine/core';
import type { ServiceCatalogItemDto } from '@expeditor/shared';
import { serviceCatalogApi } from '../../shared/api/service-catalog';

/** Админ: справочник доп. услуг (то, что клиент выбирает в поручении). */
export function ServiceCatalogPage() {
  const [items, setItems] = useState<ServiceCatalogItemDto[]>([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = () => serviceCatalogApi.listAll().then(setItems).catch(() => {});
  useEffect(() => {
    void reload();
  }, []);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await serviceCatalogApi.create({ name: name.trim() });
      setName('');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось добавить услугу');
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (it: ServiceCatalogItemDto) => {
    await serviceCatalogApi.update(it.id, { isActive: !it.isActive }).catch(() => {});
    await reload();
  };

  const rename = async (it: ServiceCatalogItemDto, newName: string) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === it.name) return;
    await serviceCatalogApi.update(it.id, { name: trimmed }).catch(() => {});
    await reload();
  };

  return (
    <Stack>
      <Title order={2}>Справочник доп. услуг</Title>
      <Text c="dimmed" size="sm">
        Эти услуги клиент выбирает в поручении (блок «Страхование и доп. услуги»).
        Снимите «Активна», чтобы скрыть услугу от клиентов, не удаляя её.
      </Text>

      <Table striped withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Название</Table.Th>
            <Table.Th style={{ width: 120 }}>Активна</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((it) => (
            <Table.Tr key={it.id}>
              <Table.Td>
                <TextInput
                  defaultValue={it.name}
                  onBlur={(e) => rename(it, e.currentTarget.value)}
                  variant="unstyled"
                />
              </Table.Td>
              <Table.Td>
                <Switch checked={it.isActive} onChange={() => toggle(it)} />
              </Table.Td>
            </Table.Tr>
          ))}
          {items.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={2}>
                <Text c="dimmed" size="sm">
                  Услуг пока нет
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      <Group align="flex-end">
        <TextInput
          label="Новая услуга"
          placeholder="Например: Страхование груза"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          style={{ flex: 1 }}
        />
        <Button onClick={add} loading={busy} disabled={!name.trim()}>
          Добавить
        </Button>
      </Group>
      {error && (
        <Text c="red" size="sm">
          {error}
        </Text>
      )}
    </Stack>
  );
}
